import i18n, { t } from "i18next";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import {
    Add,
    CloseCircle,
    Copy,
    Edit2,
    Headphone,
    Link1,
    Profile2User,
    ScanBarcode,
    Send2,
    Trash,
} from "iconsax-react";
import RowActionButton from "./RowActionButton";
import DBButton from "../../component/global/DBButton";
import DBTable from "../../component/global/DBTable/DBTable";
import DBSpinner from "../../component/global/DBSpinner";
import DBConfirmModal from "../../component/global/DBConfirmModal";
import { ToastContext } from "../../component/global/ToastContext";
import { useRequest } from "../../hooks/helperData/useRequest";
import {
    SURVEY_CAMPAIGNS,
    surveyCampaignUrl,
    surveyPublicPageUrl,
} from "../../helperData/ApiAddress";
import {
    CAMPAIGN_STATUS,
    campaignIsCancellable,
    campaignIsDeletable,
    campaignIsSendable,
    campaignKindLabel,
    campaignDeliveryLabel,
    campaignStatusLabel,
    isLinkCampaign,
    isPhoneKind,
    isPhoneCampaign,
} from "./constants";
import CampaignModal from "./CampaignModal";
import RecipientsModal from "./RecipientsModal";
import { ADDRESS_CAMPAIGN_KINDS } from "./CampaignRecipientsSection";
import { translateServerDetail } from "./translateServerDetail";
import { campaignSendQuoteSpec } from "./sendBilling";
import { isInsufficientCoins, useAiCoinGate } from "../../features/ai-billing/AiCoinGate";

export default function CampaignsPanel({
    survey,
    linkOnly = false,
    onOpenCallCenter,
}) {
    const toast = useContext(ToastContext);
    const [campaigns, setCampaigns] = useState([]);
    const [campaignModal, setCampaignModal] = useState({
        show: false,
        campaign: null,
    });
    const [recipientsModal, setRecipientsModal] = useState(null);
    const [confirmState, setConfirmState] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const { confirmAndRun, openFromError, gate } = useAiCoinGate();

    const [getCampaigns, getCampaignsLoading] = useRequest(
        SURVEY_CAMPAIGNS,
        "GET"
    );

    const authHeaders = () => ({
        Authorization: `Bearer ${sessionStorage.getItem("auth")}`,
        "Accept-Language": i18n.language || "fa",
    });

    const loadCampaigns = () => {
        getCampaigns({
            survey: survey?.id || undefined,
            page_size: 100,
            ...(linkOnly ? { scope: "personal" } : {}),
        })
            .then((r) => setCampaigns(r.results || []))
            .catch(() => setCampaigns([]));
    };

    useEffect(() => {
        loadCampaigns();
    }, [survey?.id]);

    const postAction = (campaign, action, data = {}) =>
        axios.post(surveyCampaignUrl(campaign.id, action), data, {
            headers: authHeaders(),
        });

    const executeAction = (campaign, action) => {
        setActionLoading(true);
        return postAction(campaign, action)
            .then(() => {
                if (action === "send") {
                    toast(t("survey.campaign.send.started"), "success");
                } else {
                    toast(t("survey.campaign.save.success"), "success");
                }
                setConfirmState(null);
                loadCampaigns();
            })
            .catch((e) => {
                if (action === "send" && isInsufficientCoins(e)) {
                    openFromError(e, () => executeAction(campaign, action));
                    return;
                }
                const detail = e.response?.data?.detail;
                const translated = translateServerDetail(detail, t);
                toast(
                    translated ||
                        (action === "send"
                            ? t("survey.campaign.send.error")
                            : t("survey.campaign.cancel.error.generic")),
                    "danger"
                );
                setConfirmState(null);
            })
            .finally(() => setActionLoading(false));
    };

    const handleConfirm = () => {
        const { campaign, action } = confirmState;
        if (action === "delete") {
            handleDelete(campaign);
            return;
        }
        executeAction(campaign, action);
    };

    const requestSend = (campaign) => {
        const spec = campaignSendQuoteSpec(campaign);
        if (spec) {
            confirmAndRun(spec, () => executeAction(campaign, "send"));
            return;
        }
        setConfirmState({ campaign, action: "send" });
    };

    const handleDelete = (campaign) => {
        setActionLoading(true);
        axios
            .delete(`${SURVEY_CAMPAIGNS}${campaign.id}/`, {
                headers: authHeaders(),
            })
            .then(() => {
                toast(t("survey.campaign.delete.success"), "success");
                setConfirmState(null);
                loadCampaigns();
            })
            .catch((e) => {
                const detail = translateServerDetail(
                    e.response?.data?.detail,
                    t
                );
                toast(
                    detail || t("survey.campaign.delete.error"),
                    "danger"
                );
                setConfirmState(null);
            })
            .finally(() => setActionLoading(false));
    };

    const handleDuplicate = (campaign) => {
        postAction(campaign, "duplicate")
            .then(() => {
                toast(t("survey.campaign.duplicate.success"), "success");
                loadCampaigns();
            })
            .catch((e) => {
                const detail = translateServerDetail(
                    e.response?.data?.detail,
                    t
                );
                toast(detail || t("survey.campaign.duplicate.error"), "danger");
            });
    };

    const handleCopyLink = (campaign) => {
        const url = surveyPublicPageUrl(campaign.public_url);
        navigator.clipboard
            ?.writeText(url)
            .then(() => toast(t("survey.campaign.link.copied"), "success"))
            .catch(() => toast(t("survey.campaign.link.copied"), "success"));
    };

    const handleQr = (campaign) => {
        axios
            .get(surveyCampaignUrl(campaign.id, "qr-code"), {
                headers: authHeaders(),
                responseType: "blob",
            })
            .then((r) => {
                const url = URL.createObjectURL(r.data);
                const link = document.createElement("a");
                link.href = url;
                link.download = `survey-campaign-${campaign.id}.png`;
                link.click();
                URL.revokeObjectURL(url);
            })
            .catch(() => toast(t("survey.campaign.send.error"), "danger"));
    };

    const openCampaignModal = (campaign = null) => {
        setCampaignModal({ show: true, campaign });
    };

    const headers = [
        {
            id: "name",
            title: t("survey.campaign.table.name"),
            gridTemplateColumns: "1.4fr",
        },
        ...(!survey
            ? [
                  {
                      id: "surveyTitle",
                      title: t("survey.campaign.table.survey"),
                      gridTemplateColumns: "1.2fr",
                  },
              ]
            : []),
        {
            id: "kind",
            title: t("survey.campaign.table.kind"),
            gridTemplateColumns: "1fr",
        },
        {
            id: "status",
            title: t("survey.campaign.table.status"),
            gridTemplateColumns: "0.9fr",
        },
        {
            id: "recipients",
            title: t("survey.campaign.table.recipients"),
            gridTemplateColumns: "0.7fr",
        },
        {
            id: "sent",
            title: t("survey.campaign.table.sent"),
            gridTemplateColumns: "0.7fr",
        },
        {
            id: "completed",
            title: t("survey.campaign.table.completed"),
            gridTemplateColumns: "0.7fr",
        },
        {
            id: "failed",
            title: t("survey.campaign.table.failed"),
            gridTemplateColumns: "0.6fr",
        },
        {
            id: "actions",
            title: t("survey.campaign.table.actions"),
            gridTemplateColumns: "minmax(176px, max-content)",
        },
    ];

    const rows = campaigns.map((campaign) => ({
        name: campaign.name,
        ...(!survey
            ? {
                  surveyTitle: campaign.survey_title || "—",
              }
            : {}),
        kind: (
            <div className="campaign-kind-cell">
                <span
                    className={`survey-channel-badge channel-${
                        isPhoneCampaign(campaign) ? 2 : 1
                    }`}
                >
                    {campaignDeliveryLabel(campaign.kind)}
                </span>
                {!isPhoneKind(campaign.kind) ? (
                    <span className="campaign-kind-detail">
                        {campaignKindLabel(
                            campaign.kind,
                            campaign.user_group_type
                        )}
                    </span>
                ) : null}
            </div>
        ),
        status: campaignStatusLabel(campaign.status),
        recipients: campaign.recipients_count,
        sent: campaign.sent_count,
        completed: campaign.completed_count,
        failed: campaign.failed_count,
        actions: (
            <div className="survey-row-actions">
                {isLinkCampaign(campaign.kind) && campaign.survey && (
                    <RowActionButton
                        icon={Link1}
                        label={t("survey.campaign.action.copy.link")}
                        onClick={() => handleCopyLink(campaign)}
                    />
                )}
                {isLinkCampaign(campaign.kind) && campaign.survey && (
                    <RowActionButton
                        icon={ScanBarcode}
                        label={t("survey.campaign.action.qr")}
                        onClick={() => handleQr(campaign)}
                    />
                )}
                {ADDRESS_CAMPAIGN_KINDS.includes(campaign.kind) && !linkOnly && (
                    <RowActionButton
                        icon={Profile2User}
                        label={t("survey.campaign.action.recipients")}
                        onClick={() => setRecipientsModal(campaign)}
                    />
                )}
                {isPhoneCampaign(campaign) && onOpenCallCenter && (
                    <RowActionButton
                        icon={Headphone}
                        label={t("survey.campaign.action.console")}
                        onClick={() => onOpenCallCenter(campaign)}
                    />
                )}
                <RowActionButton
                    icon={Copy}
                    label={t("survey.campaign.action.copy")}
                    onClick={() => handleDuplicate(campaign)}
                />
                {campaignIsSendable(campaign) && campaign.survey && (
                    <>
                        <RowActionButton
                            icon={Edit2}
                            label={t("survey.action.edit")}
                            onClick={() => openCampaignModal(campaign)}
                        />
                        <RowActionButton
                            icon={Send2}
                            label={
                                campaign.status === CAMPAIGN_STATUS.SENT
                                    ? t("survey.campaign.action.resend")
                                    : t("survey.campaign.action.send")
                            }
                            onClick={() => requestSend(campaign)}
                        />
                    </>
                )}
                {campaignIsCancellable(campaign) && (
                    <RowActionButton
                        icon={CloseCircle}
                        danger
                        label={t("survey.campaign.action.cancel")}
                        onClick={() =>
                            setConfirmState({ campaign, action: "cancel" })
                        }
                    />
                )}
                {campaignIsDeletable(campaign) && (
                    <RowActionButton
                        icon={Trash}
                        danger
                        label={t("survey.campaign.action.delete")}
                        onClick={() =>
                            setConfirmState({ campaign, action: "delete" })
                        }
                    />
                )}
            </div>
        ),
    }));

    return (
        <div className="campaigns-panel">
            <div className="surveys-toolbar">
                <DBButton
                    caption={
                        <>
                            <Add size={18} aria-hidden="true" />{" "}
                            {t("survey.campaign.create")}
                        </>
                    }
                    onClick={() => openCampaignModal(null)}
                />
            </div>
            {getCampaignsLoading ? (
                <DBSpinner tip={t("loading")} />
            ) : campaigns.length === 0 ? (
                <div className="surveys-empty">
                    {t("survey.campaign.empty")}
                </div>
            ) : (
                <div className="surveys-table-wrap">
                    <DBTable
                        minWidth={survey ? 860 : 980}
                        headers={headers}
                        rows={rows}
                    />
                </div>
            )}

            <CampaignModal
                show={campaignModal.show}
                survey={survey}
                campaign={campaignModal.campaign}
                linkOnly={linkOnly}
                onHide={() =>
                    setCampaignModal({ show: false, campaign: null })
                }
                onSuccess={(nextCampaign, options = {}) => {
                    loadCampaigns();
                    if (options.keepOpen && nextCampaign?.id) {
                        setCampaignModal({
                            show: true,
                            campaign: nextCampaign,
                        });
                        return;
                    }
                    setCampaignModal({ show: false, campaign: null });
                }}
            />
            {recipientsModal && (
                <RecipientsModal
                    campaign={recipientsModal}
                    onHide={() => setRecipientsModal(null)}
                    onChange={loadCampaigns}
                />
            )}
            <DBConfirmModal
                show={!!confirmState}
                onHide={() => setConfirmState(null)}
                title={
                    confirmState?.action === "delete"
                        ? t("survey.campaign.delete.confirm.title")
                        : confirmState?.action === "send"
                        ? confirmState.campaign?.status === CAMPAIGN_STATUS.SENT
                            ? t("survey.campaign.resend.confirm.title")
                            : t("survey.campaign.send.confirm.title")
                        : t("survey.campaign.cancel.confirm.title")
                }
                description={
                    confirmState?.action === "delete"
                        ? t("survey.campaign.delete.confirm.description")
                        : confirmState?.action === "send"
                        ? confirmState.campaign?.status === CAMPAIGN_STATUS.SENT
                            ? t("survey.campaign.resend.confirm.description")
                            : t("survey.campaign.send.confirm.description")
                        : t("survey.campaign.cancel.confirm.description")
                }
                btnTitle={
                    confirmState?.action === "delete"
                        ? t("survey.campaign.action.delete")
                        : confirmState?.action === "send" &&
                          confirmState.campaign?.status === CAMPAIGN_STATUS.SENT
                        ? t("survey.campaign.action.resend")
                        : t("save")
                }
                loading={actionLoading}
                onConfirm={handleConfirm}
            />
            {gate}
        </div>
    );
}
